/**
 * MultiChatView - AI Workspace Chat Interface
 * 
 * Features:
 * - Auto-streaming brief on case open (Lexy greets user)
 * - Real-time AI chat with streaming responses
 * - Document split view
 * - Auto-drafting
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, Loader2, Sparkles, Paperclip, X, File, ChevronDown, ArrowUp, FileEdit, Info, Check, AlertCircle, PanelRightOpen, PanelRightClose, SplitSquareHorizontal, FileText, CheckSquare, BookOpen, Zap, Mic } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { auth } from '../../utils/auth';
import ThreadSidebar from './ThreadSidebar';
import ChatMessage from './ChatMessage';
import VoiceMode from './VoiceMode';
import DraftPanel from './DraftPanel';
import ActionPanelWorldClass from './ActionPanelWorldClass';
import CaseStatusHeader from './CaseStatusHeader';
import DocumentViewerPanel from './DocumentViewerPanel';
import StickyBrief from './StickyBrief';
import { Switch } from '../ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { useTimeTracking } from '../../contexts/TimeTrackingContext';

// Import custom hooks
import { useChatMessages, useAutoDrafting, useFileUpload, useChatConfiguration } from './hooks';
import { streamChatMessage, saveMessage } from './hooks/streamingUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MultiChatView = ({ caseId, caseName, onSwitchCase }) => {
  const { t, i18n } = useTranslation(['workspace', 'common']);
  console.log('[MultiChatView] Rendering with caseId:', caseId);
  
  // Time tracking
  const { startCaseTracking, stopTracking, isTracking, currentCaseId } = useTimeTracking();
  
  // Use custom hooks
  const {
    messages,
    loadingMessages,
    activeThreadId,
    streamingContent,
    streamingMessageId,
    streamingContentRef,
    setActiveThreadId,
    setMessages,
    loadThreadMessages,
    createThread,
    addUserMessage,
    startStreaming,
    appendStreamingContent,
    stopStreaming,
    clearMessages
  } = useChatMessages(caseId);

  const {
    autoDrafting,
    activeDraftId,
    streamingToDraft,
    draftStreamingContent,
    draftContentRef,
    draftIdRef,
    streamingToDraftRef,
    setAutoDrafting,
    setActiveDraftId,
    shouldAutoDraft,
    resetDraftStreaming,
    startDraftStreaming,
    appendDraftContent,
    finalizeDraft
  } = useAutoDrafting();

  const {
    attachments,
    uploadingFiles,
    isDragging,
    fileInputRef,
    uploadFile,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    removeAttachment,
    clearAttachments,
    setAttachments
  } = useFileUpload(caseId);

  const {
    agents,
    models,
    selectedAgent,
    selectedModel,
    showAgentMenu,
    showModelMenu,
    toggleAgentMenu,
    toggleModelMenu,
    selectAgent,
    selectModel,
    closeMenus
  } = useChatConfiguration();

  // Local UI state
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [actionPanelCollapsed, setActionPanelCollapsed] = useState(true);
  const [caseHeaderExpanded, setCaseHeaderExpanded] = useState(false);
  
  // Voice Mode state
  const [voiceModeOpen, setVoiceModeOpen] = useState(false);
  
  // Split View state
  const [splitViewEnabled, setSplitViewEnabled] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [caseDocuments, setCaseDocuments] = useState([]);
  
  // Auto-brief state
  const [showBrief, setShowBrief] = useState(true);
  
  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const userScrolledUpRef = useRef(false);
  const chatContainerRef = useRef(null);
  const lastMessageCountRef = useRef(0);

  // Start time tracking for this case when component mounts or case changes
  useEffect(() => {
    if (caseId && caseName && currentCaseId !== caseId) {
      console.log('[MultiChatView] Starting time tracking for case:', caseName);
      startCaseTracking(caseId, caseName);
    }
  }, [caseId, caseName, currentCaseId, startCaseTracking]);

  // Load thread messages when activeThreadId changes
  useEffect(() => {
    if (activeThreadId) {
      loadThreadMessages();
    } else {
      clearMessages();
    }
  }, [activeThreadId, loadThreadMessages, clearMessages]);

  // Fetch case documents for split view
  useEffect(() => {
    const fetchCaseDocuments = async () => {
      if (!caseId) return;
      try {
        const token = auth.getToken();
        const response = await axios.get(
          `${API}/cases/${caseId}/documents`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCaseDocuments(response.data.documents || []);
      } catch (error) {
        console.error('Failed to fetch case documents:', error);
      }
    };
    fetchCaseDocuments();
  }, [caseId]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    userScrolledUpRef.current = false;
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    if (messages.length > lastMessageCountRef.current && !userScrolledUpRef.current) {
      scrollToBottom();
    }
    lastMessageCountRef.current = messages.length;
  }, [messages, scrollToBottom]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeMenus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeMenus]);

  // Voice Mode event listener
  useEffect(() => {
    const handleOpenVoiceMode = () => setVoiceModeOpen(true);
    window.addEventListener('openVoiceMode', handleOpenVoiceMode);
    return () => window.removeEventListener('openVoiceMode', handleOpenVoiceMode);
  }, []);

  const handleChatScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    userScrolledUpRef.current = !isNearBottom;
    setShowScrollButton(!isNearBottom && scrollTop < scrollHeight - clientHeight - 200);
  };

  const handleSend = useCallback(async () => {
    if (!inputValue.trim() && attachments.length === 0) return;
    
    let threadId = activeThreadId;
    
    // Create thread if needed
    if (!threadId) {
      try {
        threadId = await createThread(inputValue.substring(0, 50));
      } catch (error) {
        const createErrorMsg = i18n.language === 'en' ? 'Could not create conversation' : 'Kunne ikke opprette samtale';
        toast.error(createErrorMsg);
        return;
      }
    }

    const userContent = inputValue;
    const currentAttachments = [...attachments];
    setInputValue('');
    clearAttachments();
    setLoading(true);
    
    // Check if should auto-draft
    const triggerAutoDraft = shouldAutoDraft(userContent, selectedAgent);
    
    // Reset streaming state
    resetDraftStreaming();
    const tempAssistantMsgId = startStreaming();

    // Add user message
    addUserMessage(userContent);

    // Start polling for UI updates
    pollingIntervalRef.current = setInterval(() => {
      if (streamingContentRef.current) {
        // Force update streaming content
      }
      if (draftContentRef.current && streamingToDraftRef.current) {
        // Force update draft content
      }
    }, 50);

    try {
      const result = await streamChatMessage(
        {
          threadId,
          content: userContent,
          attachments: currentAttachments,
          autoDraft: triggerAutoDraft,
          existingDraftId: draftIdRef.current,
          lang: i18n.language === 'en' ? 'en' : 'no'
        },
        {
          onContent: (content) => {
            appendStreamingContent(content);
          },
          onDocStart: (intro) => {
            console.log('[Chat] DOC_START received, intro:', intro);
            startDraftStreaming();
            const creatingMsg = i18n.language === 'en' ? '📄 Creating document...' : '📄 Oppretter dokument...';
            toast.info(creatingMsg, { duration: 2000 });
            if (intro) {
              // Set the intro as the streaming content to be displayed in chat
              streamingContentRef.current = intro;
              // Also update the state so it renders immediately
              appendStreamingContent('');  // Trigger re-render with current ref value
            }
          },
          onDocContent: (content) => {
            appendDraftContent(content);
          },
          onDraftId: (draftId) => {
            console.log('[Chat] DRAFT_ID received:', draftId);
            finalizeDraft(draftId);
            const createdMsg = i18n.language === 'en' ? '✅ Document created' : '✅ Dokument opprettet';
            toast.success(createdMsg, { duration: 2000 });
          },
          onChatSummary: (summary) => {
            streamingContentRef.current = summary;
          },
          onError: (error) => {
            const errorMsg = i18n.language === 'en' ? 'An error occurred' : 'En feil oppstod';
            toast.error(error.message || errorMsg);
          },
          onDone: async (data) => {
            clearInterval(pollingIntervalRef.current);
            
            // Determine final content
            let finalContent = streamingContentRef.current;
            const isDraft = data.draftId || streamingToDraftRef.current;
            
            if (isDraft && draftContentRef.current) {
              // Use the AI's intro from streaming
              const intro = streamingContentRef.current;
              
              if (intro && intro.length > 10) {
                // AI provided a natural intro - just use it as-is
                // The document card will show the "click to edit" action
                finalContent = intro;
              } else {
                // Fallback if no intro from AI
                finalContent = i18n.language === 'en' 
                  ? 'I\'ve created the document for you.'
                  : 'Jeg har opprettet dokumentet for deg.';
              }
            }
            
            // Save message (non-blocking)
            const contentToSave = isDraft ? draftContentRef.current : streamingContentRef.current;
            if (contentToSave) {
              saveMessage(threadId, contentToSave, isDraft);
            }
            
            // Add final message
            stopStreaming(finalContent, {
              messageId: data.messageId,
              draftId: data.draftId
            });
            
            setLoading(false);
          }
        }
      );

      if (!result.success) {
        clearInterval(pollingIntervalRef.current);
        const errorFallback = i18n.language === 'en' 
          ? 'Sorry, an error occurred. Please try again.'
          : 'Beklager, det oppstod en feil. Prøv igjen.';
        stopStreaming(streamingContentRef.current || errorFallback);
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      const sendErrorMsg = i18n.language === 'en' ? 'Could not send message' : 'Kunne ikke sende melding';
      toast.error(sendErrorMsg);
      clearInterval(pollingIntervalRef.current);
      const errorFallback = i18n.language === 'en' 
        ? 'Sorry, an error occurred. Please try again.'
        : 'Beklager, det oppstod en feil. Prøv igjen.';
      stopStreaming(streamingContentRef.current || errorFallback);
      setLoading(false);
    }
  }, [inputValue, attachments, activeThreadId, selectedAgent, createThread, clearAttachments, shouldAutoDraft, resetDraftStreaming, startStreaming, addUserMessage, appendStreamingContent, startDraftStreaming, appendDraftContent, finalizeDraft, stopStreaming, streamingContentRef, draftContentRef, streamingToDraftRef, draftIdRef, i18n.language]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handle voice message - returns AI response for TTS
  const handleVoiceMessage = async (message: string): Promise<string> => {
    if (!message.trim()) return '';
    
    let threadId = activeThreadId;
    
    // Create thread if needed
    if (!threadId) {
      try {
        threadId = await createThread(message.substring(0, 50));
      } catch (error) {
        throw new Error('Kunne ikke opprette samtale');
      }
    }
    
    // Add user message
    addUserMessage(message.trim());
    
    try {
      const token = auth.getToken();
      const response = await axios.post(
        `${API}/ai-workspace/threads/${threadId}/chat`,
        { 
          message: message.trim(),
          attachments: [],
          agent_type: selectedAgent?.id || 'general',
          auto_drafting: shouldAutoDraft
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Get AI response text
      const aiMessage = response.data?.assistant_message?.content || 
                       response.data?.response || 
                       'Meldingen ble behandlet.';
      
      // Reload messages
      loadThreadMessages();
      
      return aiMessage;
    } catch (error) {
      console.error('Voice message error:', error);
      throw error;
    }
  };

  const handleNewThread = (thread) => {
    setActiveThreadId(thread.id);
    clearMessages();
  };

  const hasMessages = messages.length > 0;

  return (
    <>
    {/* Voice Mode Overlay */}
    <VoiceMode
      isOpen={voiceModeOpen}
      onClose={() => setVoiceModeOpen(false)}
      onSendMessage={handleVoiceMessage}
      language={i18n.language === 'nb' ? 'nb' : 'en'}
    />
    <div className="h-full flex bg-white overflow-hidden">
      {/* Thread Sidebar */}
      <ThreadSidebar
        caseId={caseId}
        activeThreadId={activeThreadId}
        onSelectThread={setActiveThreadId}
        onNewThread={handleNewThread}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <CaseStatusHeader
          caseId={caseId}
          caseName={caseName}
          isExpanded={caseHeaderExpanded}
          onToggleExpand={() => setCaseHeaderExpanded(!caseHeaderExpanded)}
          onSwitchCase={onSwitchCase}
        />
        
        {!activeThreadId || !hasMessages ? (
          <div className="flex-1 flex flex-col overflow-y-auto px-4">
            <div className="w-full max-w-3xl mx-auto py-6 flex-1 flex flex-col">
              {/* Sticky Brief - cached, on-demand AI brief */}
              {showBrief && (
                <div className="mb-4">
                  <StickyBrief
                    caseId={caseId}
                    caseName={caseName}
                    shouldCollapse={false}
                  />
                </div>
              )}
              
              {/* Spacer to push input to bottom */}
              <div className="flex-1" />
              
              {/* Chat input at bottom - same width as Case Summary */}
              <div className="w-full">
                <ChatInput
                inputValue={inputValue}
                setInputValue={setInputValue}
                handleKeyPress={handleKeyPress}
                handleSend={handleSend}
                loading={loading}
                attachments={attachments}
                uploadingFiles={uploadingFiles}
                isDragging={isDragging}
                handleDragOver={handleDragOver}
                handleDragLeave={handleDragLeave}
                handleDrop={handleDrop}
                removeAttachment={removeAttachment}
                fileInputRef={fileInputRef}
                uploadFile={uploadFile}
                inputRef={inputRef}
                agents={agents}
                selectedAgent={selectedAgent}
                showAgentMenu={showAgentMenu}
                toggleAgentMenu={toggleAgentMenu}
                selectAgent={selectAgent}
                models={models}
                selectedModel={selectedModel}
                showModelMenu={showModelMenu}
                toggleModelMenu={toggleModelMenu}
                selectModel={selectModel}
                autoDrafting={autoDrafting}
                setAutoDrafting={setAutoDrafting}
                splitViewEnabled={splitViewEnabled}
                setSplitViewEnabled={setSplitViewEnabled}
                caseDocuments={caseDocuments}
                setSelectedDocument={setSelectedDocument}
              />
              </div>
            </div>
          </div>
        ) : (
          <>
            {loadingMessages ? (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
              </div>
            ) : (
              <div 
                ref={chatContainerRef}
                className="flex-1 overflow-y-auto"
                onScroll={handleChatScroll}
              >
                <div className="max-w-3xl mx-auto px-4 lg:px-6 py-4 lg:py-6">
                  {/* Sticky Brief at top of thread */}
                  {showBrief && (
                    <div className="mb-4">
                      <StickyBrief
                        caseId={caseId}
                        caseName={caseName}
                        shouldCollapse={messages.length > 0}
                      />
                    </div>
                  )}
                  {messages.map((message, index) => (
                    <ChatMessage
                      key={message.id || index}
                      message={message}
                      caseId={caseId}
                      caseName={caseName}
                      isStreaming={message.streaming}
                      onOpenDraft={setActiveDraftId}
                      draftInfo={message.draft_id ? { id: message.draft_id, title: i18n.language === 'en' ? 'Document (draft)' : 'Dokument (utkast)', version: 1 } : null}
                    />
                  ))}
                  {streamingMessageId && (
                    <ChatMessage
                      key={streamingMessageId}
                      message={{
                        id: streamingMessageId,
                        role: 'assistant',
                        content: streamingContent,
                        created_at: new Date().toISOString()
                      }}
                      caseId={caseId}
                      caseName={caseName}
                      isStreaming={true}
                      onOpenDraft={setActiveDraftId}
                    />
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </div>
            )}

            {showScrollButton && (
              <button
                onClick={scrollToBottom}
                className="absolute bottom-24 right-6 w-10 h-10 bg-white border border-gray-200 rounded-full shadow-lg flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-all z-10"
                title="Scroll til bunnen"
              >
                <ChevronDown className="w-5 h-5" />
              </button>
            )}

            <div className="border-t border-gray-200 bg-white p-3 lg:p-4">
              <div className="max-w-3xl mx-auto">
                <ChatInput
                  inputValue={inputValue}
                  setInputValue={setInputValue}
                  handleKeyPress={handleKeyPress}
                  handleSend={handleSend}
                  loading={loading}
                  attachments={attachments}
                  uploadingFiles={uploadingFiles}
                  isDragging={isDragging}
                  handleDragOver={handleDragOver}
                  handleDragLeave={handleDragLeave}
                  handleDrop={handleDrop}
                  removeAttachment={removeAttachment}
                  fileInputRef={fileInputRef}
                  uploadFile={uploadFile}
                  inputRef={inputRef}
                  agents={agents}
                  selectedAgent={selectedAgent}
                  showAgentMenu={showAgentMenu}
                  toggleAgentMenu={toggleAgentMenu}
                  selectAgent={selectAgent}
                  models={models}
                  selectedModel={selectedModel}
                  showModelMenu={showModelMenu}
                  toggleModelMenu={toggleModelMenu}
                  selectModel={selectModel}
                  autoDrafting={autoDrafting}
                  setAutoDrafting={setAutoDrafting}
                  splitViewEnabled={splitViewEnabled}
                  setSplitViewEnabled={setSplitViewEnabled}
                  caseDocuments={caseDocuments}
                  setSelectedDocument={setSelectedDocument}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Split View Document Panel */}
      <SplitViewPanel
        splitViewEnabled={splitViewEnabled}
        selectedDocument={selectedDocument}
        setSelectedDocument={setSelectedDocument}
        setSplitViewEnabled={setSplitViewEnabled}
        caseDocuments={caseDocuments}
        caseId={caseId}
      />

      {/* Draft Panel */}
      {(activeDraftId || streamingToDraft) && (
        <DraftPanel
          draftId={streamingToDraft ? null : activeDraftId}
          onClose={() => {
            setActiveDraftId(null);
            resetDraftStreaming();
          }}
          streamingContent={draftStreamingContent}
          isStreaming={streamingToDraft}
        />
      )}
    </div>
    </>
  );
};

// ============== SUB-COMPONENTS ==============

// Chat Input Component - ChatGPT-inspired World-Class Design
const ChatInput = ({
  inputValue, setInputValue, handleKeyPress, handleSend, loading,
  attachments, uploadingFiles, isDragging,
  handleDragOver, handleDragLeave, handleDrop, removeAttachment,
  fileInputRef, uploadFile, inputRef,
  agents, selectedAgent, showAgentMenu, toggleAgentMenu, selectAgent,
  models, selectedModel, showModelMenu, toggleModelMenu, selectModel,
  autoDrafting, setAutoDrafting,
  splitViewEnabled, setSplitViewEnabled, caseDocuments, setSelectedDocument
}) => {
  const [showPromptLibrary, setShowPromptLibrary] = React.useState(false);
  const [showWorkflowLibrary, setShowWorkflowLibrary] = React.useState(false);
  const [sourcesEnabled, setSourcesEnabled] = React.useState(true);
  const [tasksEnabled, setTasksEnabled] = React.useState(false);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const { t, i18n } = useTranslation(['workspace', 'common']);

  // Auto-resize textarea
  React.useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, [inputValue]);

  const handleTextareaKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      className="relative max-w-3xl mx-auto w-full space-y-3"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drop zone overlay */}
      {isDragging && (
        <div className="absolute inset-0 bg-white/95 border-2 border-dashed border-gray-300 rounded-3xl flex items-center justify-center z-10 backdrop-blur-sm">
          <div className="text-center">
            <Paperclip className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600 font-medium">{i18n.language === 'nb' ? 'Slipp fil her' : 'Drop file here'}</p>
          </div>
        </div>
      )}

      {/* Attachments - Above input */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {attachments.map(a => (
            <div key={a.id} className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-xl text-xs group hover:bg-gray-200 transition-colors">
              <File className="w-4 h-4 text-gray-500" />
              <span className="text-gray-700 max-w-[180px] truncate font-medium">{a.filename}</span>
              <button type="button" onClick={() => removeAttachment(a.id)} className="text-gray-400 hover:text-red-500 transition-colors">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Main ChatGPT-style input container */}
      <div className="relative bg-white border border-gray-200 rounded-3xl shadow-lg shadow-gray-200/50 hover:shadow-xl hover:border-gray-300 focus-within:border-gray-300 focus-within:shadow-xl transition-all duration-200">
        {/* Textarea area */}
        <div className="relative flex items-end">
          <textarea
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleTextareaKeyDown}
            placeholder={i18n.language === 'nb' ? 'Skriv din instruks her...' : 'Write your instruction here...'}
            rows={1}
            className="flex-1 px-5 py-4 bg-transparent text-gray-900 placeholder-gray-400 resize-none text-base leading-relaxed pr-14 appearance-none border-0 ring-0 focus:ring-0 focus:outline-none"
            style={{ minHeight: '60px', maxHeight: '200px', boxShadow: 'none !important', outline: 'none !important', border: 'none' }}
          />
          
          {/* Send button - ChatGPT style circular */}
          <div className="absolute right-3 bottom-3">
            <button
              onClick={handleSend}
              disabled={loading || (!inputValue.trim() && attachments.length === 0)}
              className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 ${
                (inputValue.trim() || attachments.length > 0) && !loading
                  ? 'bg-gray-900 text-white hover:bg-gray-700 hover:scale-105 shadow-lg'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowUp className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Bottom toolbar inside input - minimal */}
        <div className="flex items-center justify-between px-4 pb-3 pt-0">
          {/* Left: File upload and Voice */}
          <div className="flex items-center gap-1">
            <input type="file" ref={fileInputRef} onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])} className="hidden" />
            <button 
              onClick={() => fileInputRef.current?.click()}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all"
              title={i18n.language === 'nb' ? 'Legg ved fil' : 'Attach file'}
            >
              <Paperclip className="w-4 h-4" />
            </button>
            {/* Voice Mode Button */}
            <button 
              onClick={() => {
                // Dispatch custom event to open voice mode
                window.dispatchEvent(new CustomEvent('openVoiceMode'));
              }}
              className="p-2 text-gray-400 hover:text-violet-600 hover:bg-violet-50 rounded-xl transition-all"
              title={i18n.language === 'nb' ? 'Talemelding' : 'Voice message'}
            >
              <Mic className="w-4 h-4" />
            </button>
          </div>

          {/* Right: Model selector */}
          <div className="relative">
            <button 
              onClick={toggleModelMenu}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-all"
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span className="font-medium">{selectedModel?.name || 'GPT-5.1'}</span>
              <ChevronDown className="w-3 h-3" />
            </button>
            {showModelMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={toggleModelMenu} />
                <div className="absolute bottom-full right-0 mb-2 w-56 bg-white border border-gray-200 rounded-2xl shadow-2xl z-50 overflow-hidden">
                  <div className="p-1.5">
                    {[
                      { id: 'gpt-5.1', name: 'GPT-5.1', desc: i18n.language === 'nb' ? 'Nyeste og beste' : 'Latest & best' },
                      { id: 'gpt-4o', name: 'GPT-4o', desc: i18n.language === 'nb' ? 'Mest kapabel' : 'Most capable' },
                      { id: 'gpt-4o-mini', name: 'GPT-4o Mini', desc: i18n.language === 'nb' ? 'Rask og effektiv' : 'Fast & efficient' },
                      { id: 'claude-3', name: 'Claude 3.5', desc: i18n.language === 'nb' ? 'God på analyse' : 'Great for analysis' }
                    ].map(model => (
                      <button 
                        key={model.id}
                        onClick={() => { selectModel(model); toggleModelMenu(); }}
                        className={`w-full text-left px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors ${selectedModel?.id === model.id ? 'bg-gray-100' : ''}`}
                      >
                        <div className="flex items-center gap-2">
                          <div className="font-medium text-sm text-gray-900">{model.name}</div>
                          {selectedModel?.id === model.id && <Check className="w-4 h-4 text-gray-900" />}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">{model.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Split View Panel Component
const SplitViewPanel = ({ splitViewEnabled, selectedDocument, setSelectedDocument, setSplitViewEnabled, caseDocuments, caseId }) => {
  if (!splitViewEnabled) return null;

  if (selectedDocument) {
    return (
      <div className="w-[400px] flex-shrink-0">
        <DocumentViewerPanel
          document={selectedDocument}
          onClose={() => { setSplitViewEnabled(false); setSelectedDocument(null); }}
          onDocumentChange={setSelectedDocument}
          availableDocuments={caseDocuments}
          caseId={caseId}
        />
      </div>
    );
  }

  if (caseDocuments.length > 0) {
    return (
      <div className="w-[400px] flex-shrink-0 flex flex-col bg-white border-l border-gray-200">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="text-sm font-medium text-gray-900">Velg dokument</h3>
          <p className="text-xs text-gray-500 mt-1">Velg et dokument å vise i split view</p>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {caseDocuments.map((doc) => (
            <button
              key={doc.id}
              onClick={() => setSelectedDocument(doc)}
              className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors text-left"
            >
              <FileText className="w-5 h-5 text-gray-400 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{doc.filename}</p>
                <p className="text-xs text-gray-500">{doc.file_type || 'Dokument'}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-[400px] flex-shrink-0 flex flex-col items-center justify-center bg-white border-l border-gray-200 p-6">
      <FileText className="w-12 h-12 text-gray-300 mb-3" />
      <p className="text-sm text-gray-600 text-center">Ingen dokumenter i saken</p>
      <p className="text-xs text-gray-400 text-center mt-1">Last opp dokumenter for å bruke split view</p>
      <button type="button" onClick={() => setSplitViewEnabled(false)} className="mt-4 text-sm text-blue-600 hover:text-blue-700">Skjul split view</button>
    </div>
  );
};

export default MultiChatView;
